import { useCallback, useEffect, useState } from "react";
import { API_ROUTES, type Survey } from "@ziolo/shared";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { useSession } from "../session/SessionProvider.js";

export interface SurveysState {
  items: Survey[];
  loading: boolean;
  error: string | null;
  add: (survey: Survey) => void;
  vote: (surveyId: number, optionId: number) => Promise<void>;
  remove: (surveyId: number) => Promise<void>;
}

export function useSurveys(): SurveysState {
  const { user } = useSession();
  const [items, setItems] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Signing in swaps the voter identity from the IP digest to the account, which can
  // change both `hasVoted` and `canDelete` on every survey in the list.
  const viewerId = user?.id ?? null;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    apiRequest<Survey[]>(API_ROUTES.surveys.list, { signal: controller.signal })
      .then((surveys) => {
        setItems(surveys);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorMessage(cause));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [viewerId]);

  const add = useCallback((survey: Survey) => {
    setItems((current) => [survey, ...current]);
  }, []);

  const vote = useCallback(async (surveyId: number, optionId: number) => {
    // The response is the survey with results revealed, so it replaces the stale copy.
    const updated = await apiRequest<Survey>(API_ROUTES.surveys.vote(surveyId), {
      method: "POST",
      body: { optionId },
    });

    setItems((current) =>
      current.map((survey) => (survey.id === surveyId ? updated : survey)),
    );
  }, []);

  const remove = useCallback(async (surveyId: number) => {
    await apiRequest(API_ROUTES.surveys.remove(surveyId), { method: "DELETE" });
    setItems((current) => current.filter((survey) => survey.id !== surveyId));
  }, []);

  return { items, loading, error, add, vote, remove };
}
